import type { Task } from '../types';

// Task-tree helpers. The tree is encoded by `task.parentTaskId` (undefined =
// top-level). Children live in the same category bucket as their parent, so
// these helpers operate on a flat Task[] (pass a category's tasks, or all of
// them — they only look at ids + parentTaskId). With no parentTaskId set
// anywhere, every task is a root and these are no-ops over a flat list.

const byOrder = (a: Task, b: Task) =>
  (a.order ?? 0) - (b.order ?? 0) ||
  (a.createdAt ?? '').localeCompare(b.createdAt ?? '');

/** Top-level tasks (no parent), sorted by order. */
export function getRootTasks(tasks: Task[]): Task[] {
  return tasks.filter((t) => !t.parentTaskId).sort(byOrder);
}

/** Direct children of a task, sorted by order. */
export function getChildren(tasks: Task[], parentId: string): Task[] {
  // ⚠️ A falsy parentId must match NOTHING. A malformed task (no `id`) is a root
  // because it has no `parentTaskId` — and without this guard `undefined ===
  // undefined` also makes it its own child, so flattenTree recurses until the stack
  // blows. One bad record used to take the whole Tasks page down; now it can't.
  if (!parentId) return [];
  return tasks.filter((t) => t.parentTaskId === parentId).sort(byOrder);
}

export function isLeaf(tasks: Task[], taskId: string): boolean {
  if (!taskId) return true;
  return !tasks.some((t) => t.parentTaskId === taskId);
}

/** All descendant tasks (recursive), excluding the task itself. */
export function getDescendants(tasks: Task[], taskId: string): Task[] {
  const out: Task[] = [];
  const walk = (id: string) => {
    for (const child of getChildren(tasks, id)) {
      out.push(child);
      walk(child.id);
    }
  };
  walk(taskId);
  return out;
}

/** The task plus all its descendants. */
export function getSubtree(tasks: Task[], taskId: string): Task[] {
  const self = tasks.find((t) => t.id === taskId);
  const rest = getDescendants(tasks, taskId);
  return self ? [self, ...rest] : rest;
}

/** Depth from root (root = 0). Guards against cycles. */
export function getDepth(tasks: Task[], taskId: string): number {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  let depth = 0;
  let current = byId.get(taskId);
  const seen = new Set<string>();
  while (current?.parentTaskId && !seen.has(current.id)) {
    seen.add(current.id);
    current = byId.get(current.parentTaskId);
    depth += 1;
    if (depth > 100) break; // hard stop on a malformed cycle
  }
  return depth;
}

export interface FlatTreeNode {
  task: Task;
  depth: number;
  hasChildren: boolean;
}

/**
 * Pre-order flatten for rendering: each root, then its children (recursively),
 * with a depth marker. `expandedIds` (if given) gates which subtrees are walked
 * — collapsed parents don't emit their descendants.
 */
export function flattenTree(
  tasks: Task[],
  expandedIds?: Set<string>,
  /** Optional sibling comparator (e.g. by due date). Defaults to manual `order`. */
  compare?: (a: Task, b: Task) => number,
): FlatTreeNode[] {
  const out: FlatTreeNode[] = [];
  const sortSibs = (arr: Task[]) => (compare ? [...arr].sort(compare) : arr);
  const walk = (nodes: Task[], depth: number) => {
    for (const task of nodes) {
      const children = getChildren(tasks, task.id);
      out.push({ task, depth, hasChildren: children.length > 0 });
      if (children.length > 0 && (!expandedIds || expandedIds.has(task.id))) {
        walk(sortSibs(children), depth + 1);
      }
    }
  };
  walk(sortSibs(getRootTasks(tasks)), 0);
  return out;
}

/**
 * Completion roll-up for a subtree, counted by LEAVES (a parent with children
 * is "done" only when all its leaf descendants are done). A leaf task counts as
 * itself. Returns { completed, total } leaf counts.
 */
export function rollUpCompletion(tasks: Task[], taskId: string): { completed: number; total: number } {
  const children = getChildren(tasks, taskId);
  if (children.length === 0) {
    const self = tasks.find((t) => t.id === taskId);
    const done = self?.completed ? 1 : 0;
    return { completed: done, total: 1 };
  }
  return children.reduce(
    (acc, child) => {
      const r = rollUpCompletion(tasks, child.id);
      return { completed: acc.completed + r.completed, total: acc.total + r.total };
    },
    { completed: 0, total: 0 },
  );
}

/** True if every leaf descendant of the task is completed. */
export function isSubtreeComplete(tasks: Task[], taskId: string): boolean {
  const { completed, total } = rollUpCompletion(tasks, taskId);
  return total > 0 && completed === total;
}
